import React, { Component, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import PropTypes from 'prop-types';

import Spinner from 'core/components/Spinner';
import ApiRequest from 'core/api/request';
import ListingCard from 'core/components/ListingCard';
import getQueryParam from 'core/utils/getQueryParam';
import { ScrollEdge } from 'core/utils';

import { ScrollWatch } from 'core/hoc';
import {
	PastBoxHeading,
	Boxes,
	BoxesWrapper,
	Box
} from 'listing/components/PastBoxes/pastBoxesStyles';

import {
	SpinWrapper,
	Title,
	Wrapper,
	ScrollWrapper,
} from './recsStyles';
import CaretBtn from 'core/components/CaretBtn';
import { Divider } from 'listing/layout';

class Recs extends Component {
	timeout = null;
	state = {
		lazyloadHandlerRegistered: false,
		loading: false,
		recs: this.props.recs || [],
	};

	lazyloadHandler = e => {
		if (! this.props?.recs?.length) {
			if (e.target.dataset.component === "Recs") {
				document.removeEventListener("lazybeforeunveil", this.lazyloadHandler);
				this.getRecs();
			}
		}
	};

	getRecs = async () => {
		if (this.props?.recs?.length) return this.props.recs;
		if (this.state.loading) return;

		this.setState({ loading: true });

		const { limit, listingId, strategy } = this.props;
		let reqParams = { limit: limit, recstrat: strategy };

		if (listingId) {
			reqParams['listing_id'] = listingId;
		}

		// pass-thru `?bust=t` if present
		if (getQueryParam('bust') === 't') {
			reqParams['bust'] = 't';
		}

		try {
			const { results } = await ApiRequest.get('/recs/', reqParams);
			this.setState({
				recs: results,
				loading: false,
			});
		} catch (e) {
			this.setState({ loading: false });
		}
	};

	componentDidMount() {
		if (! this.props?.recs?.length) {
			document.addEventListener("lazybeforeunveil", this.lazyloadHandler);
			this.setState({ lazyloadHandlerRegistered: true });
		}
	}

	componentWillUnmount() {
		if (! this.props?.recs?.length) {
			document.removeEventListener("lazybeforeunveil", this.lazyloadHandler);
		}
	}

	renderEmptyState = () => {
		if (this.props?.recs?.length) return null;

		if (this.state.loading) {
			return (
				<SpinWrapper>
					<Spinner size="lg" />
				</SpinWrapper>
			);
		}

		if (this.state.lazyloadHandlerRegistered) {
			return <div className="lazyload" data-component="Recs" />;
		}

		return null;
	};

	render() {
		if (this.state.recs.length < this.props.minimum) return this.renderEmptyState();

		const {
			className,
			title,
			renderTitle,
			isLoggedIn,
			isScrollable,
			favoriteIds,
			mobile,
			hideFav,
			strategy,
			tracking,
		} = this.props;

		const cardProps = { mobile, hideFav, isLoggedIn };

		if (!isScrollable) {
			return (
				<div className={className}>
					{renderTitle ? renderTitle() : (<Title>{title}</Title>)}
					<Wrapper>
						{this.state.recs.map((rec, idx) => (
							<ListingCard
								key={`recId-${rec.listingVersion.listingId}`}
								{...cardProps}
								listing={rec.listingVersion}
								favorited={favoriteIds.includes(rec.listingVersion.listingId)}
								lazy={true}
								queryParams={{
									...tracking,
									cn: (idx + 1),
									sn: `${strategy}-recs`,
								}}
							/>
						))}
					</Wrapper>
				</div>
			);
		}

		return (
			/* For a smaller, side-scrolling list */
			<ScrollWrapper id="box-recs-wrapper" className={className}>
				{renderTitle ? renderTitle() : (<PastBoxHeading>{title}</PastBoxHeading>)}
				<ScrollWatch>
					{({ wrapperRef, scrollPercentX, scrollTo, noScrollXYet }) => (
						<BoxesWrapper fadeOut={scrollPercentX < ScrollEdge} fadeIn={! noScrollXYet}>
							<CaretBtn center flip left="4px" onClick={scrollTo(-400)} hide={noScrollXYet} alt="Slide Forward" />
							<Boxes ref={wrapperRef}>
								<>
									{this.state.recs.map((rec, idx) => (
										<Box
											as="div"
											className="listing-card-column"
											key={`recId-${rec.listingVersion.listingId}`}>
											<ListingCard
												className="listing-card"
												{...cardProps}
												hideDescription={!!mobile}
												listing={rec.listingVersion}
												favorited={favoriteIds.includes(rec.listingVersion.listingId)}
												queryParams={{
													...tracking,
													cn: (idx + 1),
													sn: `${strategy}-recs`,
												}}
											/>
										</Box>
									))}
								</>
							</Boxes>
							<CaretBtn center right="4px" onClick={scrollTo(400)} hide={scrollPercentX > ScrollEdge} alt="Slide Forward" />
						</BoxesWrapper>
					)}
				</ScrollWatch>
				<Divider />
			</ScrollWrapper>
		);
	}

	static propTypes = {
		listingId: PropTypes.number,
		minimum: PropTypes.number,
		title: PropTypes.string,
		strategy: PropTypes.string,
		limit: PropTypes.oneOfType([
			PropTypes.number,
			PropTypes.string,
		]),
		renderTitle: PropTypes.func,
		className: PropTypes.string,
		isLoggedIn: PropTypes.bool,
		isScrollable: PropTypes.bool, // Whether list should side scroll
		mobile: PropTypes.bool,
		hideFav: PropTypes.bool,
		favoriteIds: PropTypes.arrayOf(PropTypes.number),
		tracking: PropTypes.object,
		recs: PropTypes.array,
	};

	static defaultProps = {
		// NOTE: We mostly set `aws_related_sims` as the strategy.
		// Using `featured_category` as default bc it doesn't rely on 3rd party tech
		strategy: 'featured_category',
		minimum: 2,
		title: 'Customers who bought this also bought...',
		limit: 4,
		renderTitle: null,
		favoriteIds: [],
		tracking: {},
	};
}

export default hot(module)(Recs);
